import React, { useState, useEffect } from 'react'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { generateUUID } from '@renderer/utils/idUtils'
import { useNavigate, useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'
import cogoToast from '@successtar/cogo-toast'
import { Package, RecordingType } from '@renderer/types/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { unifiedCameraUtils } from '@renderer/utils'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'

type PageState = 'welcome' | 'main-option'
type BoothMode = 'japanese' | 'photostrip' | 'flipbook'

const WelcomePage: React.FC = () => {
  const { setSessionState, uploadSessionState, logActivity } = useSessionStateStore()
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { config, getPageSettings } = useConfigStore()
  const { selectedSecondaryCamera } = useSettingsStore()
  // const welcomeSettings = getPageSettings('welcome') // Not used in booth-daughter flow
  const [isSessionStarting, setIsSessionStarting] = useState(false)
  const [currentPage, setCurrentPage] = useState<PageState>('welcome')
  const [animationTime, setAnimationTime] = useState(0)

  // Animation timer for bounce effect
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationTime((prev) => prev + 16)
    }, 16)
    return (): void => clearInterval(interval)
  }, [])

  const startSession = async (mode: 'capture' | 'record', boothMode: BoothMode): Promise<void> => {
    if (isSessionStarting) {
      return // Prevent multiple calls
    }
    setIsSessionStarting(true)

    try {
      const bypassIds = [
        '67e3f8d12bb1bd3dbad28011',
        '67e3f8e32bb1bd3dbad28012',
        '67e3f8f02bb1bd3dbad28013'
      ]

      const isBypass = bypassIds.includes(projectId || '')

      // Skip credit validation for bypass projects
      if (isBypass) {
        const sessionId = generateUUID()
        const recordingType = config?.pages?.checkout?.settings?.packages?.find(
          (pkg: Package) =>
            pkg.type === 'recording' ||
            pkg.type === 'strip_live' ||
            pkg.type === 'strip_story' ||
            pkg.type === 'session_recording'
        )?.type as RecordingType

        logActivity(`Session started in ${mode} mode with ${boothMode} booth.`)
        setSessionState({
          id: sessionId,
          projectId,
          mode,
          userPreferences: {
            useGIF: getPageSettings('capture')?.gifEnabled,
            useRecording: getPageSettings('capture')?.recordingEnabled,
            useVideo: getPageSettings('record')?.enabled,
            recordingType,
            boothMode: boothMode
          },
          timeline: {
            startTime: new Date()
          },
          gallery: {
            id: `${9999999999999 - Date.now()}-${sessionId}`
          }
        })
        let nextPage = 'frame'
        if (selectedSecondaryCamera) {
          nextPage = 'camera'
        }
        uploadSessionState()
        navigate(`/${projectId}/${nextPage}`)
        return
      }
      // Credit deduction is now handled in Capture.tsx when capture session starts
      // This ensures customers only pay when they actually start capturing

      const sessionId = generateUUID()

      // Get recording package to determine type
      const recordingType = config?.pages?.checkout?.settings?.packages?.find(
        (pkg: Package) =>
          pkg.type === 'recording' ||
          pkg.type === 'strip_live' ||
          pkg.type === 'strip_story' ||
          pkg.type === 'session_recording'
      )?.type as RecordingType

      logActivity(`Session started in ${mode} mode with ${boothMode} booth.`)
      setSessionState({
        id: sessionId,
        projectId,
        mode,
        timeline: {
          startTime: new Date()
        },
        userPreferences: {
          useGIF: getPageSettings('capture')?.gifEnabled,
          useRecording: getPageSettings('capture')?.recordingEnabled,
          useVideo: getPageSettings('record')?.enabled,
          recordingType,
          boothMode: boothMode
        },
        gallery: {
          id: `${9999999999999 - Date.now()}-${sessionId}`
        }
      })
      // Navigate to frame page
      let nextPage = 'frame'
      if (selectedSecondaryCamera && (await unifiedCameraUtils.isDeviceResponding('secondary'))) {
        nextPage = 'camera'
      }
      uploadSessionState()
      navigate(`/${projectId}/${nextPage}`)
    } catch (error) {
      console.error('Error starting session:', error)
      cogoToast.error('Failed to check credits. Please try again.')
    } finally {
      setIsSessionStarting(false)
    }
  }

  const handleStartCapture = async (): Promise<void> => {
    logActivity('User clicked on "Start Capture" button')
    setCurrentPage('main-option')
  }

  const handleOptionSelect = async (option: BoothMode): Promise<void> => {
    logActivity(`User selected ${option} option`)

    let mode: 'capture' | 'record' = 'capture'
    if (option === 'flipbook') {
      mode = 'record'
    }

    await startSession(mode, option)
  }

  // Bounce animation calculation
  const duration = 1000
  const progress = (animationTime % duration) / duration
  const bounceY = Math.sin(progress * Math.PI * 2) * 20

  // Call all hooks at the top level
  const cachedMainOptionBg = useCachedBackgroundImage('/assets/boothndaughter/main-option.webp')
  const cachedJapaneseBg = useCachedBackgroundImage('/assets/boothndaughter/japanese.webp')
  const cachedPhotostripBg = useCachedBackgroundImage('/assets/boothndaughter/photostrip.webp')
  const cachedFlipbookBg = useCachedBackgroundImage('/assets/boothndaughter/flipbook.webp')
  const cachedWelcomeBg = useCachedBackgroundImage('/assets/boothndaughter/welcome.webp')
  const cachedStartBg = useCachedBackgroundImage('/assets/boothndaughter/start.webp')

  if (currentPage === 'main-option') {
    return (
      <div
        className="w-full h-full relative overflow-hidden"
        style={{
          backgroundColor: config?.theme?.backgroundColor || '#FBFBFB',
          backgroundImage: `url(${cachedMainOptionBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Japanese Option - Left Center */}
        <div className="absolute left-[100px] top-[300px]">
          <button
            onClick={() => handleOptionSelect('japanese')}
            disabled={isSessionStarting}
            className="relative drop-shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              width: '473px',
              height: '538px',
              backgroundImage: `url(${cachedJapaneseBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              border: 'none',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            {isSessionStarting && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
                <div className="text-white text-2xl font-bold">Starting...</div>
              </div>
            )}
          </button>
          <div className="text-center mt-4">
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '48px',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              Rp86.000
            </span>
          </div>
        </div>

        {/* Photostrip Option - Center Bottom */}
        <div className="absolute left-1/2 bottom-20 transform -translate-x-1/2">
          <button
            onClick={() => handleOptionSelect('photostrip')}
            disabled={isSessionStarting}
            className="relative drop-shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              width: '473px',
              height: '538px',
              backgroundImage: `url(${cachedPhotostripBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              border: 'none',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            {isSessionStarting && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
                <div className="text-white text-2xl font-bold">Starting...</div>
              </div>
            )}
          </button>
          <div className="text-center mt-4">
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '48px',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              Rp65.000
            </span>
          </div>
        </div>

        {/* Flipbook Option - Right Center (Coming Soon) */}
        <div className="absolute right-[100px] top-[300px]">
          <button
            disabled={true}
            className="relative drop-shadow-2xl opacity-50 cursor-not-allowed"
            style={{
              width: '473px',
              height: '538px',
              backgroundImage: `url(${cachedFlipbookBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              border: 'none',
              outline: 'none'
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 rounded-lg">
              <div className="text-white text-xl font-bold">Coming Soon</div>
            </div>
          </button>
          <div className="text-center mt-4">
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '48px',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                opacity: '0.5'
              }}
            >
              Rp127.000
            </span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundColor: config?.theme?.backgroundColor || '#FBFBFB',
        backgroundImage: `url(${cachedWelcomeBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Start Capture Button with Bounce Animation */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2">
        <button
          onClick={handleStartCapture}
          disabled={isSessionStarting}
          className="relative shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            width: '386px',
            height: '137px',
            backgroundImage: `url(${cachedStartBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            border: 'none',
            outline: 'none',
            cursor: 'pointer',
            transform: `translateY(${bounceY}px)`
          }}
        >
          {isSessionStarting && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
              <div className="text-white text-2xl font-bold">Starting...</div>
            </div>
          )}
        </button>
      </div>
    </div>
  )
}

export default WelcomePage
